import type {
  ShortcutTooltipContentProps,
  SoftphoneShortcutTooltipProps,
} from './index.types';
import { ANALYTICS_EVENT_SOFTPHONE } from 'Modules/softphone/constants/analytics';
import { getShortcutKeys } from 'Modules/softphone/constants/keyboardShortcuts';
import { TOOLTIP_DELAY_MS } from 'Modules/softphone/constants/timing';
import React from 'react';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { Kbd, KbdGroup } from '~/lib/ui/kbd';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/lib/ui/tooltip';

export type {
  ShortcutTooltipContentProps,
  SoftphoneShortcutTooltipProps,
} from './index.types';

/**
 * What a shortcut tooltip *says* — "<label> <Kbd chips>" — separated from the
 * `Tooltip` that shows it, so a caller that must own the surrounding `Tooltip`
 * can still render this wording inside it.
 *
 * `SoftphoneActionButton` is that caller: it keeps one `Tooltip` across every
 * state so the button underneath is never unmounted, and swaps only the
 * content. See its note on focus.
 */
export const ShortcutTooltipContent = ({
  action,
  label,
}: ShortcutTooltipContentProps): React.ReactElement => {
  const keys = getShortcutKeys(action);

  return (
    <span className="flex items-center gap-2 text-[12px] whitespace-nowrap">
      <span>{label}</span>
      {keys.length > 0 && (
        <KbdGroup>
          {keys.map((key, index) => (
            <React.Fragment key={`${key}-${index}`}>
              {index > 0 && <span aria-hidden="true">+</span>}
              <Kbd>{key}</Kbd>
            </React.Fragment>
          ))}
        </KbdGroup>
      )}
    </span>
  );
};

/** Fires `shortcut_tooltip_viewed` as the tooltip opens, never as it closes. */
export const reportShortcutTooltipViewed = (
  action: SoftphoneShortcutTooltipProps['action'],
  open: boolean
): void => {
  if (open) {
    pushToGTMDataLayer(ANALYTICS_EVENT_SOFTPHONE.SHORTCUT_TOOLTIP_VIEWED, {
      action,
    });
  }
};

/**
 * Inline discoverability tooltip for a softphone call-action shortcut
 * (CUU2-3208, FR1): shows "<label> <Kbd chips>" on hover/focus of `children`
 * and fires `shortcut_tooltip_viewed` the first time it opens.
 */
export const SoftphoneShortcutTooltip = ({
  action,
  label,
  children,
  side = 'bottom',
  sideOffset,
}: SoftphoneShortcutTooltipProps): React.ReactElement => (
  <Tooltip
    delayDuration={TOOLTIP_DELAY_MS}
    onOpenChange={(open) => reportShortcutTooltipViewed(action, open)}
  >
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side={side} sideOffset={sideOffset}>
      <ShortcutTooltipContent action={action} label={label} />
    </TooltipContent>
  </Tooltip>
);
